import type { Issue } from './types';

/**
 * HOW LONG A CARD HAS SAT WHERE IT IS — and whether that is longer than this
 * board's own norm.
 *
 * The card-display review's headline signal: a board says what is being worked
 * on but never how long it has been stuck mid-flight, so a card three days in
 * progress and one thirty days in progress look identical. This reads the time
 * a card ENTERED its working state (`startedAt`, clamped server-side in 0033)
 * and turns it into a quiet marker — muted until it crosses the board's norm,
 * then shown in the amber the priority mark already uses (no new colour).
 *
 * ⚠️ THE NORM IS THE BOARD'S, NOT A NUMBER PICKED HERE. A fixed "old after N
 * days" is wrong on both a fast board (everything flags) and a slow one (nothing
 * does). The threshold is the 75th percentile of what is currently in flight,
 * with a floor so a board where the work is genuinely quick does not start
 * flagging ordinary two-day cards.
 */

/** The states that count as "sitting in a working column" — where aging means
 *  something. A backlog or done card is not aging; it is parked or finished. */
const WORKING: ReadonlySet<string> = new Set(['inprogress', 'staging', 'inreview']);

/** The floor under the board norm, in days. Nothing is "aged" below this, even
 *  on a board whose every card turns around in a day — a two-day card is not a
 *  problem worth a colour. A working week is the smallest span that reads as
 *  "this has been going a while" for ICT rollout work. */
export const AGING_FLOOR_DAYS = 7;

const DAY = 86_400_000;

/** Whole days between two instants, floored, never negative. Null when the start
 *  is missing (a card with no recorded working-start cannot be aged). */
export function ageInDays(startedAt: number | undefined | null, now: number): number | null {
  if (!startedAt) return null;
  return Math.max(0, Math.floor((now - startedAt) / DAY));
}

/** True when a card is in a working state that aging applies to. */
export function isWorking(issue: Pick<Issue, 'status'>): boolean {
  return WORKING.has(issue.status);
}

/** The ages, in days, of every card currently in flight with a known start —
 *  the sample the board norm is drawn from. */
export function workingAges(issues: Issue[], now: number): number[] {
  const out: number[] = [];
  for (const i of issues) {
    if (!isWorking(i)) continue;
    const a = ageInDays(i.startedAt, now);
    if (a !== null) out.push(a);
  }
  return out;
}

/**
 * The board's own "this is taking a while" line: the 75th percentile of what is
 * in flight, never below the floor. With too few cards in flight to have a norm
 * (under four), the floor stands alone — a "percentile" of two cards is noise.
 */
export function agingThreshold(ages: number[]): number {
  if (ages.length < 4) return AGING_FLOOR_DAYS;
  const sorted = [...ages].sort((a, b) => a - b);
  // Nearest-rank 75th percentile: the smallest value with at least 75% at or
  // below it.
  const idx = Math.min(sorted.length - 1, Math.ceil(0.75 * sorted.length) - 1);
  return Math.max(AGING_FLOOR_DAYS, sorted[idx]);
}

export interface CardAge {
  /** Whole days in the current working state. */
  days: number;
  /** Past the board's norm — draw it in the amber, not the muted ink. */
  aged: boolean;
}

/**
 * The aging marker for one card, or null when it does not apply (not in a
 * working state, or no recorded start). `all` is the whole board, so the norm
 * is measured against the cards actually in flight beside it.
 */
export function agingOf(issue: Issue, all: Issue[], now: number): CardAge | null {
  if (!isWorking(issue)) return null;
  const days = ageInDays(issue.startedAt, now);
  if (days === null) return null;
  const threshold = agingThreshold(workingAges(all, now));
  return { days, aged: days > threshold };
}
